import { useTranslation } from 'react-i18next';
import {
  Avatar,
  AvatarFallback,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Separator,
} from '@parrot-software/design-system';
import { cn } from '@parrot-software/utils';

interface LoyaltyCardProps {
  className?: string;
}

const STAMPS = [
  { id: 'stamp-1', filled: true },
  { id: 'stamp-2', filled: true },
  { id: 'stamp-3', filled: true },
  { id: 'stamp-4', filled: false },
  { id: 'stamp-5', filled: false },
];

/**
 * Loyalty card shown in the referral hero, built from design-system primitives
 * (Card, Avatar for the stamps, Separator). Purely decorative — no interaction.
 *
 * @param {LoyaltyCardProps} props - Component properties
 * @returns {React.ReactElement} The loyalty card illustration
 */
export const LoyaltyCard = ({ className }: LoyaltyCardProps) => {
  const { t } = useTranslation('referral');

  return (
    <Card className={cn('w-full max-w-sm rotate-1 overflow-hidden shadow-xl', className)}>
      <CardHeader className="flex-row items-center justify-between space-y-0 bg-primary p-5">
        <span className="text-lg font-bold lowercase italic text-primary-foreground">{t('loyaltyCard.brand')}</span>
        <span className="text-xs uppercase tracking-widest text-primary-foreground/80">{t('loyaltyCard.badge')}</span>
      </CardHeader>

      <CardContent className="flex flex-col gap-5 p-5">
        <CardTitle className="text-xl">{t('loyaltyCard.title')}</CardTitle>

        <div className="flex items-end justify-between gap-2">
          {STAMPS.map((stamp) => (
            <div className="flex flex-col items-center gap-1.5" key={stamp.id}>
              <Avatar
                className={cn('h-11 w-11', {
                  'border border-primary/30 bg-primary/5': stamp.filled,
                  'border border-dashed border-border': !stamp.filled,
                })}
              >
                <AvatarFallback className="bg-transparent text-[10px] font-bold lowercase italic text-primary">
                  {stamp.filled ? t('loyaltyCard.brand') : ''}
                </AvatarFallback>
              </Avatar>
              <span className="text-[10px] text-muted-foreground">{t('loyaltyCard.stampLabel')}</span>
            </div>
          ))}

          <div className="flex flex-col items-center gap-1.5">
            <Avatar className="h-11 w-11">
              <AvatarFallback className="flex-col bg-primary text-primary-foreground">
                <span className="text-[9px] font-bold leading-none">{t('loyaltyCard.rewardAmount')}</span>
                <span className="text-[8px] leading-none">{t('loyaltyCard.rewardPrice')}</span>
              </AvatarFallback>
            </Avatar>
            <span className="text-[10px] text-muted-foreground">{t('loyaltyCard.rewardCaption')}</span>
          </div>
        </div>

        <Separator />

        <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('loyaltyCard.footnote')}</p>
      </CardContent>
    </Card>
  );
};
